/** @jsxImportSource preact */
import { createContext, type ComponentChildren } from "preact";
import { ok } from "neverthrow";
import { createPortal } from "preact/compat";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";

import type {
  Conversation,
  ConversationState,
} from "../../conversation/conversation";
import * as m from "../../paraglide/messages.js";
import { PanelSheetHostContext } from "../sheet/panel-sheet";
import type { WidgetTokens } from "../theme";
import {
  SupportRequestCardView,
  SupportRequestContext,
} from "./support-request-card";
import { useSupportFeedback } from "./support-feedback";

/** 产品默认：仅实时 Handoff 自动弹出，恢复与回放没有此事件。 */
function openOnLiveHandoff(): boolean {
  return true;
}
interface RequestPanel {
  message?: ConversationState["messages"][number];
  dismiss: () => void;
}
const RequestPanelContext = createContext<RequestPanel | undefined>(undefined);

/** 面板选择只属于本次 UI；输入仍由会话的 supportEdits 持有。 */
export function SupportRequestPanelProvider({
  conversation,
  state,
  children,
}: {
  conversation: Conversation;
  state: ConversationState;
  children: ComponentChildren;
}) {
  const scope = `${state.conversationId ?? ""}:${state.auth.store_login}`;
  const [selection, setSelection] = useState<{
    messageId: string;
    scope: string;
  }>();
  const trigger = useRef<HTMLElement>();
  const wasSelected = useRef(false);
  useEffect(() => {
    if (wasSelected.current && !selection && trigger.current?.isConnected)
      trigger.current.focus({ preventScroll: true });
    wasSelected.current = selection !== undefined;
  }, [selection]);
  useEffect(() => {
    setSelection((current) => (current?.scope === scope ? current : undefined));
  }, [scope]);
  const feedback = useSupportFeedback();
  const [verified, setVerified] = useState<{ email?: string }>();
  useEffect(() => {
    if (!verified) return;
    feedback?.show(
      verified.email
        ? m.widget_order_verified_detail({ email: verified.email })
        : "",
      "default",
      { title: m.widget_order_verified(), duration: 3000 },
    );
    setVerified(undefined);
  }, [verified, feedback]);
  useLayoutEffect(
    () =>
      conversation.subscribeUI((event) => {
        if (event.kind === "handoff") {
          if (!openOnLiveHandoff()) return;
          // 买家正在填或正在发另一条请求时不打断，新请求在消息里留入口。
          setSelection((current) =>
            current?.scope === scope
              ? current
              : { messageId: event.messageId, scope },
          );
        } else {
          setVerified({ email: event.email });
        }
      }),
    [conversation, scope, feedback],
  );
  const message =
    selection?.scope === scope
      ? state.messages.find((row) => row.id === selection.messageId)
      : undefined;
  return (
    <SupportRequestContext.Provider
      value={{
        operate: conversation.operateSupportRequest,
        edit: conversation.editSupportRequest,
        edits: state.supportEdits,
        open: (messageId, ref, orderReference) => {
          const root = document.activeElement;
          const active = root?.shadowRoot?.activeElement ?? root;
          // 浮层里的 Try again 也走这里；只记浮层外的入口，关闭时焦点才能回去。
          if (!message && active instanceof HTMLElement)
            trigger.current = active;
          setSelection({ messageId, scope });
          const existing = state.messages.find((row) => row.id === messageId)
            ?.supportRequests?.[0];
          return existing
            ? Promise.resolve(ok(existing))
            : conversation.openSupportRequest(messageId, ref, orderReference);
        },
        canSend: state.supportRequestsAvailable,
        chatBusy: state.busy,
      }}
    >
      <RequestPanelContext.Provider
        value={{
          message,
          dismiss: () => {
            setSelection(undefined);
          },
        }}
      >
        {children}
      </RequestPanelContext.Provider>
    </SupportRequestContext.Provider>
  );
}

/** 宿主处于聊天窗的 PanelSheetHost 内，整个会话只有一份请求浮层。 */
export function SupportRequestPanel({ tokens }: { tokens: WidgetTokens }) {
  const panel = useContext(RequestPanelContext);
  const host = useContext(PanelSheetHostContext);
  const message = panel?.message;
  if (!panel || !message || !host) return null;
  const card = message.supportRequests?.[0];
  return createPortal(
    <SupportRequestCardView
      key={message.id}
      card={card}
      opening={{
        messageId: message.id,
        pending: !card,
        ...message.supportOpening,
      }}
      tokens={tokens}
      onDismiss={panel.dismiss}
    />,
    host.element,
  );
}
